import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { CreateCalculusConditions } from '@retail/calculus/types';
import {
  BulkEditPricesSchema,
  BulkEditSalesPriceTable,
  PriceItemSchema,
} from '@retail/my-assortment/components';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { getISO_8601Date } from '@retail/utils';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

interface Props {
  itemsForEdit: AssortmentPrices[];
  includeVat: boolean;
  createMultipleConditions: (conditions: CreateCalculusConditions[]) => void;
  handleRemoveItem: (itemId: number) => void;
  onClose: () => void;
}

export function BulkEditSalesPricesDialog({
  itemsForEdit,
  includeVat,
  createMultipleConditions,
  handleRemoveItem,
  onClose,
}: Props) {
  const t = useAppTFunction();

  const { handleSubmit, setValue, getValues } =
    useFormContext<BulkEditPricesSchema<PriceItemSchema>>();

  const getMarkupConditions = useCallback(
    (schemaItems: BulkEditPricesSchema<PriceItemSchema>) => {
      const itemEntries = Object.entries(schemaItems.items);
      const conditions: CreateCalculusConditions[] = itemEntries
        .map(([mgSupplierItemId, formItem]) => {
          return formItem.salesPrice && mgSupplierItemId
            ? {
                content: {
                  markupTargetPrice: {
                    targetPrice: formItem.salesPrice,
                    incMva: includeVat,
                    unit: formItem.primaryUnit,
                  },
                },
                binding: { mgSupplierItem: { supplierItemId: Number(mgSupplierItemId) } },
                validFrom: getISO_8601Date(formItem.fromDate),
              }
            : undefined;
        })
        .filter((condition) => !!condition);
      return conditions;
    },
    [includeVat]
  );
  const onRemoveItem = useCallback(
    (mgSupplierItemId: number) => {
      handleRemoveItem(mgSupplierItemId);
      const formRows = getValues('items');
      delete formRows[mgSupplierItemId];
      setValue('items', formRows);
    },
    [getValues, handleRemoveItem, setValue]
  );

  const createItemSalesPriceConditions = handleSubmit((schemaItems) => {
    const conditions = getMarkupConditions(schemaItems);
    createMultipleConditions(conditions);
    onClose();
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>{t('myAssortment.actions.editSeveralPrices')}</DialogTitle>
      <DialogContent className="padding-0">
        <BulkEditSalesPriceTable
          items={itemsForEdit}
          includeVat={includeVat}
          handleRemoveItem={onRemoveItem}
        />
      </DialogContent>
      <DialogActions>
        <TextButton variant="text" size="small" onClick={onClose}>
          {t('common.cancel')}
        </TextButton>

        <ContainedButton
          size="small"
          color="secondary"
          disabled={itemsForEdit.length === 0}
          onClick={createItemSalesPriceConditions}
        >
          {t('myAssortment.actions.addToDrawer')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
